<template>
	<div :class="classes">
		<span
			v-for="( user, index ) in maxUsers"
			:key="user.personId"
			:title="userName(user)"
			@click="onAvatarClicked($event, user)"
			@mouseover="onAvatarMouseOver($event, user)"
			:class="{
				'user-avatar-spread__avatar': true,
				'user-avatar-spread__avatar--first': index === 0,
			}"
			:style="`height:${ avatarSize };margin-left:${ avatarMargin }px;`"
		>
			<UserAvatar
				:class-name="isForefrontAvatar( user ) ? 'user-avatar-spread__avatar--forefront' : ''"
				:size="avatarSize"
				:style="avatarStyle"
				:user="user"
			/>
			<FontAwesomeIcon
				:icon="['fad', 'check-circle']"
				:size="badgeSize"
				:class="{
					'user-avatar-spread__positive-badge': true,
					'user-avatar-spread__positive-badge--visible': isPositiveBadgeVisible( user ),
				}"
			/>
		</span>
		<span v-if="exceedsMaxAvatarLength"
			class="user-avatar-spread__avatar"
			:style="`height:${ avatarSize };margin-left:${ avatarMargin }px;`"
		>
			<UserAvatar
				:size="avatarSize"
				:style="avatarStyle"
			>
				<template #initials>{{ `+${ hiddenUserCount }` }}</template>
			</UserAvatar>
		</span>
	</div>
</template>

<script>
import InviteeRoleType from "@teaming/common-enums/InviteeRoleType";
import UserAvatar from "./UserAvatar";

const MAX_AVATARS = 9;
export const OVERLAP_MODE_TIGHT = "tight";
export const OVERLAP_MODE_NONE = "none";

const isValidOverlapMode = mode => {
	return [
		OVERLAP_MODE_TIGHT,
		OVERLAP_MODE_NONE,
	].includes( mode );
};

const BadgeSizePixelThreshold = {
	SMALL: 24,
	LARGE: 32,
};

const PX_PER_REM = 16;

export default {
	name: "UserAvatarSpread",
	components: {
		UserAvatar,
	},
	props: {
		users: {
			type: Array,
			default: () => [],
		},
		positiveBadgeUserIds: {
			type: Array,
			default: () => [],
			required: false,
		},
		avatarSize: {
			type: String,
			default: "1.5rem",
		},
		focusAvatarOnHover: {
			type: Boolean,
			default: false,
		},
		overlapMode: {
			type: String,
			default: OVERLAP_MODE_TIGHT,
		},
		/**
		 * Maybe be either "tight" or "none". Invalid values will
		 *   be interpreted as "tight".
		 */
		maxAvatars: {
			type: Number,
			default: MAX_AVATARS,
		},
		className: {
			type: String,
			default: "",
		},
		border: {
			type: Boolean,
			default: false,
		},
	},
	data: () => {
		return {
			forefrontAvatarPersonId: "",
		};
	},
	computed: {
		classes() {
			return {
				"user-avatar-spread": true,
				[ this.className ]: !!this.className,
			};
		},
		avatarPixelSize() {
			const numericSize = parseFloat( this.avatarSize ) || 0;
			if ( this.avatarSize.endsWith( "em" ) ) { // em, rem
				return ( numericSize * PX_PER_REM );
			}
			return numericSize;
		},
		badgeSize() {
			const numericSize = this.avatarPixelSize;
			if ( numericSize < BadgeSizePixelThreshold.SMALL ) {
				return "sm";
			}
			if ( numericSize > BadgeSizePixelThreshold.LARGE ) {
				return "lg";
			}
			return "1x";
		},
		avatarMargin() {
			if ( this.users.length < 2 ) {
				return 0;
			}
			const FRACTION = 0.20;
			const marginSize = this.avatarPixelSize * FRACTION;
			let selectedOverlapMode = this.overlapMode;
			if ( !isValidOverlapMode( selectedOverlapMode ) ) {
				selectedOverlapMode = OVERLAP_MODE_TIGHT;
			}
			// eslint-disable-next-line no-magic-numbers
			return ( selectedOverlapMode === OVERLAP_MODE_NONE ) ? marginSize : -marginSize;
		},
		maxUsers() {
			return this.users.slice( 0, this.maxAvatars );
		},
		exceedsMaxAvatarLength() {
			return this.users.length > this.maxAvatars;
		},
		hiddenUserCount() {
			return Math.max( 0, this.users.length - this.maxAvatars );
		},
		avatarStyle() {
			if ( !this.border ) {
				return "";
			}
			return `border: 0.0625rem solid ${ this.white }`;
		},
	},
	methods: {
		isForefrontAvatar( user ) {
			return this.forefrontAvatarPersonId === user.personId;
		},
		onAvatarClicked( e, user ) {
			this.$emit( "click", e );
			this.$emit( "userclick", { ...user, } );
		},
		onAvatarMouseOver( e, user ) {
			if ( !this.focusAvatarOnHover ) {
				return;
			}
			this.$emit( "mouseover", e );
			this.forefrontAvatarPersonId = user.personId;
		},
		userName( user ) {
			let name = user.displayName;
			if ( user?.role === InviteeRoleType.ORGANIZER ) {
				name = `${ name } (${ this.$t( "common.UserAvatarSpread_organizer" ) })`;
			}
			return name.trim();
		},
		isPositiveBadgeVisible( user ) {
			return this.positiveBadgeUserIds.includes( user.personId );
		},
	},
};
</script>

<style lang="scss" scoped>
.user-avatar-spread {
	display: inline-flex;
}

.user-avatar-spread__avatar {
	display: inline-block;
	position: relative;
	z-index: 1;
}

.user-avatar-spread__avatar--first {
	margin-left: 0 !important;
}

.user-avatar-spread__avatar--forefront {
	z-index: 2;
}

.user-avatar-spread__positive-badge {
	display: none;
	position: absolute;
	bottom: 0;
	right: 0;

	--fa-primary-color: #{$user-avatar-spread__positive-badge-primary-color};
	--fa-secondary-color: #{$user-avatar-spread__positive-badge-secondary-color};
	--fa-secondary-opacity: 1;
}

.user-avatar-spread__positive-badge--visible {
	display: inline-block;
}

.user-initials {
	transform: scale(0.8);
	cursor: default;
	text-align: center;
	display: inline-block;
	width: 100%;
}
</style>
